import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { waLink } from "../lib/site";

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="group inline-flex items-center">
    <img
      src="/images/logo-light.png"
      alt="Mamma Rosa"
      className="h-10 md:h-12 w-auto object-contain transition-transform group-hover:scale-[1.04]"
    />
  </Link>
);

const LangSwitcher = ({ onPick }) => {
  const { lang, setLang, langs } = useLang();
  return (
    <div className="flex flex-wrap items-center gap-x-0.5 gap-y-1 max-w-[220px] lg:max-w-none justify-end" data-testid="lang-switcher">
      {langs.map((l) => (
        <button
          key={l.code}
          data-testid={`lang-${l.code}`}
          onClick={() => {
            setLang(l.code);
            onPick && onPick();
          }}
          className={`px-2 py-1 text-xs tracking-widest font-body transition-colors ${
            lang === l.code ? "text-gold" : "text-cream/50 hover:text-cream"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/menu", label: t("nav.menu") },
    { to: "/story", label: t("nav.story") },
    { to: "/gallery", label: t("nav.gallery") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const book = waLink("Ciao Mamma Rosa! Vorrei prenotare un tavolo.");

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-[100] transition-all duration-500 ${
          scrolled
            ? "bg-navy-deep/80 backdrop-blur-xl border-b border-white/10 py-3"
            : "bg-transparent py-5"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between">
          <Logo />

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label}`}
                className={({ isActive }) =>
                  `font-body text-sm tracking-wide link-underline transition-colors ${
                    isActive ? "text-gold" : "text-cream/80 hover:text-cream"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <LangSwitcher />
            <a
              href={book}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-btn"
              className="group relative overflow-hidden rounded-full bg-gold px-6 py-2.5 text-navy-deep font-body text-sm font-semibold tracking-wide transition-transform hover:scale-[1.03]"
            >
              {t("nav.book")}
            </a>
          </div>

          <button
            className="lg:hidden text-cream"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99] lg:hidden bg-navy-deep/98 backdrop-blur-xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex-1 flex flex-col justify-center px-8 gap-2">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-link-${l.label}`}
                    className={({ isActive }) =>
                      `font-display text-5xl tracking-tight py-2 block ${
                        isActive ? "text-gold" : "text-cream"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
            <div className="px-8 pb-12 flex items-center justify-between border-t border-white/10 pt-6">
              <LangSwitcher />
              <a
                href={book}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-book-btn"
                className="rounded-full bg-gold px-7 py-3 text-navy-deep font-body font-semibold"
              >
                {t("nav.book")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
